import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EditIcon from '@mui/icons-material/Edit';
import licenseComplianceService from '../services/licenseComplianceService';
import type { ComplianceSetupStateDto, LicenseSetupEntryDto } from '../services/licenseComplianceService';
import userService, { type UserLicenseDto } from '../services/userService';
import { getApiErrorInfo } from '../services/api';
import LicenseRequirementCard from '../components/compliance/LicenseRequirementCard';
import PrimaryLicenseChip from '../components/compliance/PrimaryLicenseChip';
import { newLicensePulseSx } from '../components/compliance/newLicensePulse';
import LicenseEditorDialog from '../components/compliance/LicenseEditorDialog';
import DisclaimerNote from '../components/compliance/DisclaimerNote';
import { BOARD_AUTHORITY_DISCLAIMER } from '../components/compliance/disclaimers';
import { hideHoursBasedEthics } from '../components/compliance/ethicsDisplay';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { US_STATES } from '../components/profile/profileConstants';
import { formatUtcDate } from '../utils/dateTime';

function stateName(code: string): string {
  return US_STATES.find((s) => s.code === code)?.name || code;
}

/**
 * License management page (/app/licenses).
 * One card per license: license identity and edit/delete actions in the card
 * header, CPE requirement setup below. Progress against these requirements
 * is shown on the Reporting page.
 */
export default function LicenseManagement() {
  const [setupState, setSetupState] = useState<ComplianceSetupStateDto | null>(null);
  const [userLicenses, setUserLicenses] = useState<UserLicenseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<UserLicenseDto | null>(null);

  // A just-created license lands in its alphabetical slot, which can be below
  // the fold. Scroll it into view and pulse its card so the user sees where it
  // went and what to do next (review + confirm requirements).
  const [newLicenseId, setNewLicenseId] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<UserLicenseDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [batchOpen, setBatchOpen] = useState(false);
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchError, setBatchError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoadError(null);
    const [setupResult, licensesResult] = await Promise.allSettled([
      licenseComplianceService.getSetupState(),
      userService.getLicenses(),
    ]);
    if (setupResult.status === 'fulfilled') setSetupState(setupResult.value);
    if (licensesResult.status === 'fulfilled') setUserLicenses(licensesResult.value);
    if (licensesResult.status === 'rejected') {
      setLoadError('Failed to load your licenses.');
    } else if (setupResult.status === 'rejected') {
      setLoadError(getApiErrorInfo(setupResult.reason).message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Requirement mutations (prefill, confirm, subject requirements) only touch
  // setup state; the license list itself is maintained locally on CRUD.
  const refreshSetup = useCallback(async () => {
    try {
      setSetupState(await licenseComplianceService.getSetupState());
    } catch {
      // Keep showing the last known setup state; the next action will retry.
    }
  }, []);

  // Ref callback: fires when the highlighted card's wrapper mounts or the ref
  // attaches, i.e. once the new license is actually rendered in position.
  const scrollNewIntoView = useCallback((node: HTMLDivElement | null) => {
    if (!node || typeof node.scrollIntoView !== 'function') return; // jsdom lacks scrollIntoView
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    node.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
  }, []);

  // Let the pulse finish, then drop the highlight state (the animation ends
  // transparent, so removal is invisible).
  useEffect(() => {
    if (!newLicenseId) return;
    const timer = window.setTimeout(() => setNewLicenseId(null), 4000);
    return () => window.clearTimeout(timer);
  }, [newLicenseId]);

  const setupEntries = setupState?.licenses ?? [];
  const licenseById = new Map(userLicenses.map((l) => [l.id, l]));
  // Names the home state in reciprocity election copy on each card.
  const primaryEntry = setupEntries.find((e) => e.isPrimary);
  const primaryStateLabel = primaryEntry ? primaryEntry.stateName || primaryEntry.stateCode : null;
  // Licenses the setup endpoint doesn't know about yet (e.g. just created)
  // still get a card so the list never silently drops a license.
  const metadataOnlyLicenses = userLicenses.filter(
    (l) => !setupEntries.some((e) => e.licenseId === l.id)
  );
  const isEmpty = setupEntries.length === 0 && metadataOnlyLicenses.length === 0;

  // Batch confirm covers licenses whose state defaults are already loaded and
  // just awaiting review; licenses still needing prefill are left alone.
  // Licenses whose window is a fallback estimate (missing birth date / issue
  // date anchor) are excluded — mass-confirming a known guess would hurt.
  const batchEligible = setupEntries.filter(
    (e): e is LicenseSetupEntryDto & { requirement: NonNullable<LicenseSetupEntryDto['requirement']> } =>
      e.status === 'unconfirmed' && !!e.requirement && !e.missingPeriodAnchor
  );

  const openAdd = () => {
    setEditingLicense(null);
    setEditorOpen(true);
  };

  const openEdit = (license: UserLicenseDto) => {
    setEditingLicense(license);
    setEditorOpen(true);
  };

  const handleEditorSaved = async (license: UserLicenseDto, created: boolean) => {
    setUserLicenses((prev) =>
      created ? [...prev, license] : prev.map((l) => (l.id === license.id ? license : l))
    );
    setEditorOpen(false);
    if (created) {
      setSnackMessage(
        `${stateName(license.stateCode)} license added. Review and confirm its requirements.`
      );
      // Wait for the card to land in its sorted position before drawing
      // attention to it, so the scroll targets where it actually settles.
      await refreshSetup();
      setNewLicenseId(license.id);
    } else {
      setSnackMessage('License updated');
      void refreshSetup();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await userService.deleteLicense(deleteTarget.id);
      setUserLicenses((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      setSetupState((prev) =>
        prev ? { ...prev, licenses: prev.licenses.filter((e) => e.licenseId !== deleteTarget.id) } : prev
      );
      setDeleteTarget(null);
      setSnackMessage('License deleted');
      void refreshSetup();
    } catch {
      setDeleteError('Failed to delete license. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleBatchConfirm = async () => {
    setBatchSaving(true);
    setBatchError(null);
    // Send the original values (timestamps verbatim) with confirm:true so the
    // server's prefill-match detection sees an exact, unedited confirmation.
    const results = await Promise.allSettled(
      batchEligible.map((entry) =>
        licenseComplianceService.updateRequirement(entry.licenseId, {
          totalHours: entry.requirement.totalHours,
          ethicsHours: entry.requirement.ethicsHours,
          periodStart: entry.requirement.periodStart,
          periodEnd: entry.requirement.periodEnd,
          confirm: true,
        })
      )
    );
    const failedStates = batchEligible
      .filter((_, i) => results[i].status === 'rejected')
      .map((e) => e.stateName || e.stateCode);
    if (failedStates.length > 0) {
      setBatchError(`Couldn't confirm ${failedStates.join(', ')}. The others were confirmed.`);
    } else {
      setBatchOpen(false);
      setSnackMessage(`Requirements confirmed for ${batchEligible.length} licenses`);
    }
    await refreshSetup();
    setBatchSaving(false);
  };

  return (
    <Box>
      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            License Management
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: '75ch' }}>
            Confirm the CPE requirements tracked for each license. View progress on the{' '}
            <Link component={RouterLink} to="/app/cpe-history">
              Reporting page
            </Link>
            , or see the{' '}
            <Link component={RouterLink} to="/app/faq#compliance-tracking">
              compliance tracking FAQ
            </Link>
            .
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={openAdd} sx={{ flexShrink: 0 }}>
          Add license
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => { setLoading(true); void loadData(); }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : isEmpty ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Requirements will appear here once you add a license.
          </Typography>
          <Button component={RouterLink} to="/app/onboarding" variant="outlined" size="small">
            Use guided setup
          </Button>
        </Box>
      ) : (
        <>
          {batchEligible.length >= 2 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2,
                py: 1.5,
                mb: 2,
              }}
            >
              <Typography variant="body2">
                {batchEligible.length} licenses have state defaults loaded and ready to review.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={() => {
                  setBatchError(null);
                  setBatchOpen(true);
                }}
              >
                Confirm all defaults
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {setupEntries.map((entry) => {
              const license = licenseById.get(entry.licenseId) ?? null;
              return (
                <Box
                  key={entry.licenseId}
                  ref={entry.licenseId === newLicenseId ? scrollNewIntoView : undefined}
                >
                  <LicenseRequirementCard
                    entry={entry}
                    license={license}
                    onSaved={refreshSetup}
                    collapsible
                    showDisclaimer={false}
                    primaryStateLabel={primaryStateLabel}
                    highlight={entry.licenseId === newLicenseId}
                    onEditLicense={license ? () => openEdit(license) : undefined}
                    onDeleteLicense={
                      license
                        ? () => {
                            setDeleteError(null);
                            setDeleteTarget(license);
                          }
                        : undefined
                    }
                  />
                </Box>
              );
            })}

            {metadataOnlyLicenses.map((license) => (
              <Box
                key={license.id}
                ref={license.id === newLicenseId ? scrollNewIntoView : undefined}
              >
              <Card variant="outlined" sx={license.id === newLicenseId ? newLicensePulseSx : undefined}>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {stateName(license.stateCode)}
                        </Typography>
                        {license.isPrimary && <PrimaryLicenseChip />}
                        <Chip
                          label={license.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          sx={license.isActive
                            ? { bgcolor: 'status.completed.bg', color: 'status.completed.fg', fontWeight: 500 }
                            : { bgcolor: 'status.enrolled.bg', color: 'status.enrolled.fg', fontWeight: 500 }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {license.licenseNumber ? `License ${license.licenseNumber}` : 'CPA license'}
                      </Typography>
                      {(license.issueDate || license.expirationDate) && (
                        <Typography variant="caption" color="text.secondary">
                          {license.issueDate && `Issued: ${formatUtcDate(license.issueDate)}`}
                          {license.issueDate && license.expirationDate && ' · '}
                          {license.expirationDate && `Expires: ${formatUtcDate(license.expirationDate)}`}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Edit license">
                        <IconButton size="small" aria-label="Edit license" onClick={() => openEdit(license)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete license">
                        <IconButton
                          size="small"
                          aria-label="Delete license"
                          onClick={() => {
                            setDeleteError(null);
                            setDeleteTarget(license);
                          }}
                          sx={{ '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              </Box>
            ))}
          </Box>

          <DisclaimerNote sx={{ mt: 3 }}>{BOARD_AUTHORITY_DISCLAIMER}</DisclaimerNote>
        </>
      )}

      <LicenseEditorDialog
        open={editorOpen}
        license={editingLicense}
        defaultIsPrimary={userLicenses.length === 0}
        onClose={() => setEditorOpen(false)}
        onSaved={handleEditorSaved}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete license?"
        message={`Your ${stateName(deleteTarget?.stateCode ?? '')} license and its CPE requirements will no longer be tracked. Credits you've earned are not affected. This cannot be undone.`}
        loading={deleting}
        error={deleteError}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />

      {/* Batch confirm: review every default in one place, confirm in one action */}
      <Dialog open={batchOpen} onClose={batchSaving ? undefined : () => setBatchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm state defaults?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following requirements will be confirmed exactly as loaded from state data.
            You can still edit any of them afterwards.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {batchEligible.map((entry) => (
              <Box key={entry.licenseId}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {entry.stateName || entry.stateCode}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {entry.requirement.totalHours} total hrs ·{' '}
                  {!hideHoursBasedEthics(entry.requirement.ethicsHours, entry.subjectRequirements) &&
                    `${entry.requirement.ethicsHours} ethics · `}
                  {formatUtcDate(entry.requirement.periodStart)} – {formatUtcDate(entry.requirement.periodEnd)}
                </Typography>
              </Box>
            ))}
          </Box>
          {batchError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {batchError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchOpen(false)} disabled={batchSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBatchConfirm}
            disabled={batchSaving || batchEligible.length === 0}
            startIcon={batchSaving ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
          >
            Confirm {batchEligible.length} licenses
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackMessage}
        autoHideDuration={5000}
        onClose={() => setSnackMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackMessage(null)} severity="success" sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
